import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../auth/AuthContext';
import { getCobrosPorCliente } from '../../services/cobranzaService';
import { ChipEstadoCobro } from './estadoCobro';
import { PAGO_TODO, esPagoTodo } from './resumenCobro';
import ModalDetalleCobro from './ModalDetalleCobro';
import ModalNuevoCobro from './ModalNuevoCobro';

const formatoMoneda = (v) => (v === null || v === undefined ? '—' : Number(v).toLocaleString('es-AR', { style: 'currency', currency: 'ARS', maximumFractionDigits: 0 }));

/**
 * Cobros del cliente, en su ficha. Es el único acceso a los cobros cargados sin trámite: no
 * tienen ficha de trámite desde donde abrirse (ver TarjetaCobroTramite), así que sin esto solo
 * se llegaría a ellos desde los listados.
 */
// Cobranzas está en prueba: hoy la ven ADMIN y PROGRAMADOR (ver PERMISOS_EXCLUSIVOS en
// auth/roles.js). El gateo va acá adentro y no en cada ficha que monta el componente: son once
// vistas de trámite más la del cliente, y alcanzaba con olvidarse de una para que la sección
// quedara visible.
export default function TarjetaCobrosCliente({ cuil }) {
    const { puede } = useAuth();
    const puedeVerCobranzas = puede && (puede('cobranzas.cobrar') || puede('cobranzas.editar'));
    // Crear un cobro a mano es configurarlo: solo administración (16-oct-2026).
    const puedeCrear = puede && puede('cobranzas.editar');

    const [cobros, setCobros] = useState([]);
    const [cargando, setCargando] = useState(true);
    const [idCobroAbierto, setIdCobroAbierto] = useState(null);
    const [modalNuevoAbierto, setModalNuevoAbierto] = useState(false);

    const cargar = useCallback(() => {
        if (!cuil) return;
        setCargando(true);
        getCobrosPorCliente(cuil)
            .then((data) => setCobros(Array.isArray(data) ? data : []))
            .catch(() => setCobros([]))
            .finally(() => setCargando(false));
    }, [cuil]);

    useEffect(() => { if (puedeVerCobranzas) cargar(); }, [cargar, puedeVerCobranzas]);

    if (!puedeVerCobranzas) return null;
    if (!cuil || cargando) return null;

    return (
        <div className="tramites-section">
            <div className="tramites-header-row d-flex align-items-center justify-content-between">
                <h2 className="tramites-section-title">
                    <i className="bi bi-cash-coin" />
                    Cobros
                    {cobros.length > 0 && <span className="tramites-count">{cobros.length}</span>}
                </h2>
                {puedeCrear && (
                    <button className="btn btn-outline-primary btn-sm" onClick={() => setModalNuevoAbierto(true)}>
                        <i className="bi bi-plus-lg me-1"></i>Nuevo cobro
                    </button>
                )}
            </div>

            {cobros.length === 0 ? (
                <p className="text-muted mb-0">Este cliente todavía no tiene cobros cargados.</p>
            ) : (
                <div className="d-flex flex-column gap-2">
                    {cobros.map((c) => (
                        <div
                            key={c.idCobro}
                            role="button"
                            tabIndex={0}
                            onClick={() => setIdCobroAbierto(c.idCobro)}
                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setIdCobroAbierto(c.idCobro); }}
                            className="d-flex align-items-center justify-content-between flex-wrap gap-2"
                            style={{ cursor: 'pointer', border: '1px solid #dee2e6', borderRadius: '10px', padding: '0.75rem 1rem', background: '#f9fafb' }}
                        >
                            <div className="d-flex align-items-center gap-2 flex-wrap">
                                <ChipEstadoCobro estado={c.estado} />
                                <span style={{ color: '#374151' }}>
                                    {(c.tramites || []).map((t) => t.tipoTramiteNombre).filter(Boolean).join(', ') || 'Sin trámite enganchado'}
                                </span>
                            </div>
                            <span style={{ color: '#6b7280', fontSize: '0.9rem' }}>{esPagoTodo(c) ? PAGO_TODO : `Saldo: ${formatoMoneda(c.saldo)}`}</span>
                        </div>
                    ))}
                </div>
            )}

            <ModalDetalleCobro
                open={!!idCobroAbierto}
                idCobro={idCobroAbierto}
                onClose={() => setIdCobroAbierto(null)}
                onCambio={cargar}
            />
            <ModalNuevoCobro
                open={modalNuevoAbierto}
                cuil={cuil}
                onClose={() => setModalNuevoAbierto(false)}
                onCreado={cargar}
            />
        </div>
    );
}
